/**
 * ETH/ARX表示の小数点桁数（最大精度: 18桁）
 * 全画面で統一的に使用
 */
export const TOKEN_DECIMAL_PLACES = 18

/**
 * 文字列の数値に3桁区切りのカンマを付けて表示用に整形
 * - 小数部はそのまま保持し、末尾の0も削除しない
 */
export const formatWithCommas = (value: string): string => {
  if (!value) return ''
  const [intPart, decPart] = value.split('.')
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt
}

/**
 * ETH金額をフォーマット（18桁精度、末尾の0は削除）
 * @param value ETH金額（number）
 * @param options オプション
 */
export const formatEthDisplay = (
  value: number,
  options?: {
    /** 単位（ETH）を付けるか */
    withUnit?: boolean
    /** 符号を付けるか */
    withSign?: boolean
  }
): string => {
  const { withUnit = false, withSign = false } = options || {}

  // 18桁精度でフォーマットし、末尾の0を削除
  let formatted = Math.abs(value)
    .toLocaleString('ja-JP', {
      maximumFractionDigits: TOKEN_DECIMAL_PLACES,
      minimumFractionDigits: 1,
    })
    .replace(/\.?0+$/, '')

  if (withSign) {
    const sign = value >= 0 ? '+' : '-'
    formatted = `${sign}${formatted}`
  }

  if (withUnit) {
    formatted = `${formatted} ETH`
  }

  return formatted
}

// 後方互換性のため残す（既存コードが参照している場合）
export const ETH_DECIMAL_PLACES = {
  BALANCE_HOME: TOKEN_DECIMAL_PLACES,
  BALANCE_TRANSFER: TOKEN_DECIMAL_PLACES,
  BALANCE_PURCHASE: TOKEN_DECIMAL_PLACES,
  PURCHASE_AMOUNT: TOKEN_DECIMAL_PLACES,
  HISTORY: TOKEN_DECIMAL_PLACES,
  MONITORING: TOKEN_DECIMAL_PLACES,
} as const

/**
 * トークン数量のフォーマット
 * - ETH, ARXともにDecimal18のため、18桁まで表示する
 */
export const formatTokenQuantity = (value: number) => {
  return Math.abs(value)
    .toLocaleString('ja-JP', {
      maximumFractionDigits: 18,
      minimumFractionDigits: 1,
    })
    .replace(/\.?0+$/, '') // 末尾の0を削除
}

/**
 * 主要金額のフォーマット（+ または - の記号付き）
 */
export const formatPrimaryAmount = (amount: number, symbol: string) => {
  const sign = amount >= 0 ? '+' : '-'
  const quantity = formatTokenQuantity(amount)
  const trimmedSymbol = symbol.trim()

  // 非常に小さい値の場合は「0.000001ETH未満」のような形式にする
  if (Number(quantity) < 0.000001) {
    return `${sign} 0.000001 ${trimmedSymbol ? trimmedSymbol : ''} 未満`
  }

  return `${sign} ${quantity}${trimmedSymbol ? ` ${trimmedSymbol}` : ''}`
}

/**
 * 時間ラベルのフォーマット（相対時間表示）
 */
export const formatTimeLabel = (date: Date) => {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  if (!Number.isFinite(diffMinutes)) return ''
  if (diffMinutes < 1) return '1m'
  if (diffMinutes < 60) return `${diffMinutes}m`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h`
  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
  }).format(date)
}

const isSameDay = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate()

const isYesterday = (target: Date, reference: Date) => {
  const yesterday = new Date(reference)
  yesterday.setDate(reference.getDate() - 1)
  return isSameDay(target, yesterday)
}

export const formatSectionLabel = (date: Date) => {
  const now = new Date()
  if (isSameDay(date, now)) return '今日'
  if (isYesterday(date, now)) return '昨日'
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(date)
}
